import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import type { ITrajectoryDirectoriesService } from '../types/host-services';

export type TUomSelectOption = {
  label: string;
  value: number;
};

const CLASS_OF_UNIT = 'Common_ClassOfUnit';
const COMMON_UOM = 'Common_Uom';
const COMMON_ORGANIZATION = 'Common_Organization';

const DEPTH_SLUG = 'well-depth';
const SEVERITY_SLUG = 'dogleg-severity';
const COORDINATES_SLUG = 'map-coordinates';

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, unknown>;
}

function readString(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];

  if (typeof value !== 'string' || value.trim() === '') {
    return null;
  }

  return value;
}

function readNumberArray(record: Record<string, unknown>, key: string): number[] {
  const value = record[key];

  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((item): item is number => typeof item === 'number');
}

function matchesUomName(record: Record<string, unknown>, name: string): boolean {
  const normalized = name.toLowerCase();
  const candidates = [readString(record, 'name'), readString(record, 'abbreviation')];

  return candidates.some((item) => item?.toLowerCase() === normalized);
}

function buildUomLabel(record: Record<string, unknown>): string | null {
  return readString(record, 'abbreviation') ?? readString(record, 'name');
}

export class TrajectoryUomOptionsStore {
  @observable private depthOptions: TUomSelectOption[] = [];
  @observable private severityOptions: TUomSelectOption[] = [];
  @observable private coordinatesOptions: TUomSelectOption[] = [];
  @observable private organizationSelectOptions: TUomSelectOption[] = [];
  @observable private depthDefaultUomId: number | null = null;
  @observable private uomLabelById = new Map<number, string>();
  @observable private organizationLabelById = new Map<number, string>();
  @observable private isLoading = false;
  @observable private isLoaded = false;

  constructor(private readonly directories: ITrajectoryDirectoriesService | null) {
    makeObservable(this);
  }

  @computed
  get depthUomOptions(): TUomSelectOption[] {
    return this.depthOptions;
  }

  @computed
  get severityUomOptions(): TUomSelectOption[] {
    return this.severityOptions;
  }

  @computed
  get coordinatesUomOptions(): TUomSelectOption[] {
    return this.coordinatesOptions;
  }

  @computed
  get organizationOptions(): TUomSelectOption[] {
    return this.organizationSelectOptions;
  }

  @computed
  get defaultDepthUomId(): number | null {
    return this.depthDefaultUomId;
  }

  @computed
  get areOptionsLoading(): boolean {
    return this.isLoading;
  }

  @computed
  get areOptionsLoaded(): boolean {
    return this.isLoaded;
  }

  getUomLabel(uomId: number | null | undefined): string | null {
    if (uomId == null) {
      return null;
    }

    return this.uomLabelById.get(uomId) ?? null;
  }

  getOrganizationLabel(organizationId: number | null | undefined): string | null {
    if (organizationId == null) {
      return null;
    }

    return this.organizationLabelById.get(organizationId) ?? null;
  }

  @action.bound
  private setLoading(isLoading: boolean): void {
    this.isLoading = isLoading;
  }

  async load(): Promise<void> {
    if (this.isLoaded || !this.directories) {
      return;
    }

    this.setLoading(true);

    try {
      await this.directories.loadObjects([CLASS_OF_UNIT, COMMON_UOM, COMMON_ORGANIZATION]);
      const classOfUnits = this.directories.getObject(CLASS_OF_UNIT) ?? [];
      const units = this.directories.getObject(COMMON_UOM) ?? [];
      const organizations = this.directories.getObject(COMMON_ORGANIZATION) ?? [];
      const labelById = new Map<number, string>();
      const organizationOptions: TUomSelectOption[] = [];
      const organizationLabelById = new Map<number, string>();

      organizations.forEach((organization) => {
        const id = Number(organization.id);
        const name = readString(asRecord(organization.data) ?? {}, 'name');

        if (!Number.isFinite(id) || !name) {
          return;
        }

        organizationOptions.push({ label: name, value: id });
        organizationLabelById.set(id, name);
      });

      units.forEach((unit) => {
        const data = asRecord(unit.data);

        if (!data) {
          return;
        }

        const label = buildUomLabel(data);

        if (!label) {
          return;
        }

        labelById.set(Number(unit.id), label);
      });

      const findClassBySlug = (slug: string) =>
        classOfUnits.find((item) => readString(asRecord(item.data) ?? {}, 'slug') === slug) ?? null;

      const buildOptionsForSlug = (slug: string): TUomSelectOption[] => {
        const classOfUnit = findClassBySlug(slug);
        const data = asRecord(classOfUnit?.data);

        if (!data) {
          return [];
        }

        const unitIds = readNumberArray(data, 'unitIds');
        const options: TUomSelectOption[] = [];

        unitIds.forEach((unitId) => {
          const unit = units.find((item) => Number(item.id) === unitId);
          const unitData = asRecord(unit?.data);

          if (!unitData) {
            return;
          }

          const label = buildUomLabel(unitData);

          if (!label) {
            return;
          }

          options.push({ label, value: unitId });
        });

        return options;
      };

      const depthOptions = buildOptionsForSlug(DEPTH_SLUG);
      const severityOptions = buildOptionsForSlug(SEVERITY_SLUG);
      const coordinatesOptions = buildOptionsForSlug(COORDINATES_SLUG);

      const depthDefaultUomId =
        depthOptions.find((option) => option.label.toLowerCase() === 'm')?.value ??
        units.find((unit) => matchesUomName(asRecord(unit.data) ?? {}, 'm'))?.id ??
        null;

      runInAction(() => {
        this.depthOptions = depthOptions;
        this.severityOptions = severityOptions;
        this.coordinatesOptions = coordinatesOptions;
        this.organizationSelectOptions = organizationOptions;
        this.depthDefaultUomId = typeof depthDefaultUomId === 'number' ? depthDefaultUomId : null;
        this.uomLabelById = labelById;
        this.organizationLabelById = organizationLabelById;
        this.isLoaded = true;
        this.isLoading = false;
      });
    } catch (error) {
      console.error(error);
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }
}
